import React, { useState, useEffect } from 'react';
import { parseSpeakingStrategy, SentenceAnnotation, ChunkAnnotation, ParsedAnnotation } from '../../utils/speakingAnnotationParser';
import { coursesService } from '../../services/coursesService';
import './AnnotationParserTestPage.css';

// ── Render annotated chunk text with highlights ─────────────

function highlightAnnotations(chunkText: string, annotations: ParsedAnnotation[]): React.ReactNode {
  if (annotations.length === 0) return <span>{chunkText}</span>;

  // Sort annotations by position in text (find index)
  const sorted = annotations
    .map(ann => ({
      ...ann,
      startIdx: chunkText.toLowerCase().indexOf(ann.word.toLowerCase()),
    }))
    .filter(a => a.startIdx !== -1)
    .sort((a, b) => a.startIdx - b.startIdx);

  if (sorted.length === 0) return <span>{chunkText}</span>;

  const parts: React.ReactNode[] = [];
  let cursor = 0;

  for (const ann of sorted) {
    // Skip overlapping
    if (ann.startIdx < cursor) continue;

    // Text before annotation
    if (ann.startIdx > cursor) {
      parts.push(<span key={`t-${cursor}`}>{chunkText.slice(cursor, ann.startIdx)}</span>);
    }

    const endIdx = ann.startIdx + ann.word.length;
    const annotatedText = chunkText.slice(ann.startIdx, endIdx);

    parts.push(
      <span key={`a-${ann.startIdx}`} className={`highlight-${ann.type}`} title={ann.hint || ann.type}>
        {annotatedText}
        {ann.type === 'link' && <span className="link-arc" />}
        {ann.hint && <span className="hint-badge">{ann.hint}</span>}
      </span>
    );

    cursor = endIdx;
  }

  // Remaining text
  if (cursor < chunkText.length) {
    parts.push(<span key={`t-${cursor}`}>{chunkText.slice(cursor)}</span>);
  }

  return <>{parts}</>;
}

// ── Annotation badge ─────────────────────────────────────────

function AnnotationBadge({ ann }: { ann: ParsedAnnotation }) {
  const icons: Record<string, string> = {
    stress: '💪',
    link: '🔗',
    pause: '⏸',
    reduction: '🔻',
    intonation: '↕️',
    ipa: '🔤',
    syllables: '📊',
  };

  return (
    <span className={`ann-badge ann-badge-${ann.type}`}>
      {icons[ann.type] || '⚪'} {ann.type}: <strong>{ann.word}</strong>
      {ann.hint && <span className="ann-hint"> ({ann.hint})</span>}
    </span>
  );
}

// ── Chunk card ────────────────────────────────────────────────

function ChunkCard({ chunk, idx }: { chunk: ChunkAnnotation; idx: number }) {
  const hasAnnotations = chunk.annotations.length > 0 || chunk.intonation || chunk.pause;

  return (
    <div className={`chunk-card ${hasAnnotations ? 'has-annotations' : 'no-annotations'}`}>
      <div className="chunk-header">
        <span className="chunk-idx">#{idx + 1}</span>
        <span className="chunk-text-display">
          {highlightAnnotations(chunk.chunkText, chunk.annotations)}
        </span>
        {chunk.intonation && (
          <span className={`intonation-badge ${chunk.intonation}`}>
            {chunk.intonation === 'rising' ? '↗' : '↘'}
          </span>
        )}
        {chunk.pause && <span className="pause-badge">⏸</span>}
      </div>

      <div className="chunk-translation">{chunk.chunkTranslation}</div>

      <div className="chunk-focus">
        <span className="focus-label">Focus:</span> {chunk.chunkFocus}
      </div>

      {chunk.annotations.length > 0 && (
        <div className="chunk-annotations">
          {chunk.annotations.map((ann, i) => (
            <AnnotationBadge key={i} ann={ann} />
          ))}
        </div>
      )}

      {!hasAnnotations && (
        <div className="no-extract">⚠️ No annotations extracted from focus text</div>
      )}
    </div>
  );
}

// ── Sentence card ─────────────────────────────────────────────

function SentenceCard({ data, idx }: { data: SentenceAnnotation; idx: number }) {
  const totalAnnotations = data.chunks.reduce((sum, c) => sum + c.annotations.length, 0);
  const chunksWithAnnotations = data.chunks.filter(c => c.annotations.length > 0 || c.intonation || c.pause).length;

  return (
    <div className="sentence-card-test">
      <div className="sentence-header">
        <span className="sentence-idx">Sentence {idx + 1}</span>
        <div className="sentence-stats">
          <span className="stat">{totalAnnotations} annotations</span>
          <span className="stat">{chunksWithAnnotations}/{data.chunks.length} chunks parsed</span>
        </div>
      </div>

      {/* Full sentence with inline annotations */}
      <div className="sentence-full">
        {data.tone && <span className="tone-label">{data.tone}</span>}
        {data.chunks.map((chunk, i) => (
          <span key={i} className="sentence-chunk-inline">
            {highlightAnnotations(chunk.chunkText, chunk.annotations)}
            {chunk.intonation === 'rising' && <span className="intonation-inline rising">↗</span>}
            {chunk.intonation === 'falling' && <span className="intonation-inline falling">↘</span>}
            {chunk.pause && <span className="pause-inline">‖</span>}
            {i < data.chunks.length - 1 && ' '}
          </span>
        ))}
      </div>

      {/* Tips */}
      <div className="sentence-tips">{data.tips}</div>

      {/* Chunk details */}
      <div className="chunks-grid">
        {data.chunks.map((chunk, i) => (
          <ChunkCard key={i} chunk={chunk} idx={i} />
        ))}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────

export default function AnnotationParserTestPage() {
  const [courseId, setCourseId] = useState('31e2a478-f7f3-46f5-917a-19d49a9ef9fb');
  const [inputId, setInputId] = useState(courseId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<SentenceAnnotation[]>([]);
  const [courseTitle, setCourseTitle] = useState('');

  // Stats
  const totalSentences = results.length;
  const totalAnnotations = results.reduce(
    (sum, s) => sum + s.chunks.reduce((cs, c) => cs + c.annotations.length, 0), 0
  );
  const totalChunks = results.reduce((sum, s) => sum + s.chunks.length, 0);
  const parsedChunks = results.reduce(
    (sum, s) => sum + s.chunks.filter(c => c.annotations.length > 0 || c.intonation || c.pause).length, 0
  );

  const loadCourse = async (id: string) => {
    setLoading(true);
    setError(null);
    setResults([]);

    try {
      const data = await coursesService.getFullCourse(id);
      setCourseTitle(data.course?.title || id);

      const sentences = (data as any).sentences || [];
      const parsed: SentenceAnnotation[] = [];

      for (const sentence of sentences) {
        const result = parseSpeakingStrategy(sentence.text, sentence.speaking_strategy);
        if (result) {
          parsed.push(result);
        }
      }

      setResults(parsed);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load course');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCourse(courseId);
  }, [courseId]);

  return (
    <div className="annotation-parser-test">
      <h1>Speaking Annotation Parser Test</h1>
      <p className="subtitle">Parsing speaking_strategy focus text → visual annotations</p>

      {/* Course ID input */}
      <div className="course-input">
        <input
          type="text"
          value={inputId}
          onChange={(e) => setInputId(e.target.value)}
          placeholder="Course ID"
        />
        <button onClick={() => setCourseId(inputId)}>Load Course</button>
      </div>

      {loading && <div className="loading">Loading course data...</div>}
      {error && <div className="error-msg">{error}</div>}

      {results.length > 0 && (
        <>
          {/* Summary stats */}
          <div className="stats-bar">
            <h2>{courseTitle}</h2>
            <div className="stats-row">
              <div className="stat-item">
                <span className="stat-value">{totalSentences}</span>
                <span className="stat-label">Sentences</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{totalAnnotations}</span>
                <span className="stat-label">Annotations</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{parsedChunks}/{totalChunks}</span>
                <span className="stat-label">Chunks Parsed</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{totalChunks > 0 ? Math.round(parsedChunks / totalChunks * 100) : 0}%</span>
                <span className="stat-label">Coverage</span>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="legend">
            <span className="legend-item"><span className="highlight-stress">stress</span></span>
            <span className="legend-item"><span className="highlight-link">link<span className="link-arc" /></span></span>
            <span className="legend-item"><span className="highlight-reduction">reduction</span></span>
            <span className="legend-item"><span className="highlight-ipa">IPA</span></span>
            <span className="legend-item"><span className="pause-inline">‖</span> pause</span>
            <span className="legend-item"><span className="intonation-inline rising">↗</span> rising</span>
            <span className="legend-item"><span className="intonation-inline falling">↘</span> falling</span>
          </div>

          {/* Results */}
          {results.map((data, i) => (
            <SentenceCard key={i} data={data} idx={i} />
          ))}
        </>
      )}
    </div>
  );
}
